import { Action, asyncDelay } from '@weblite/logic-toolkit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { onboardingStorage } from './storage'
import {
  getOnboardingActionsReq,
  setOnboardingActionsReq,
} from '$logic/onboarding/service'
import { ActionsState, onboardingActions, IOnboarding } from './types'

const actionsGlobalState: ActionsState = {
  actions: new Set<string>(),
  fetched: false,
}

const onboardingStateUpdatedAction = new Action<ActionsState>()

export function useOnboardingActions() {
  const [actions, setActions] = useState(actionsGlobalState)

  const fetchActions = useCallback(async (): Promise<boolean> => {
    if (actions.fetched) return true
    const actionNames = onboardingActions

    const localPersistedActions = onboardingStorage.getActions()

    if (!localPersistedActions || localPersistedActions.length === 0) {
      try {
        const response = await getOnboardingActionsReq(actionNames)
        const fetchedActions = response.map(action => action.action)
        void onboardingStorage.addActions(fetchedActions)
        actionsGlobalState.fetched = true
        actionsGlobalState.actions = new Set([...fetchedActions])
        onboardingStateUpdatedAction.invoke({ ...actionsGlobalState })
        return true
      } catch (err) {
        return false
      }
    }

    const localPersistedActionsSet = new Set(localPersistedActions)

    const toFetchActions = actionNames.filter(
      actionName => !localPersistedActionsSet.has(actionName),
    )

    if (toFetchActions.length === 0) {
      actionsGlobalState.fetched = true
      actionsGlobalState.actions = new Set([...localPersistedActions])
      onboardingStateUpdatedAction.invoke({ ...actionsGlobalState })
      return true
    }

    try {
      const response = await getOnboardingActionsReq(toFetchActions)
      const fetchedActions = response.map(action => action.action)

      void onboardingStorage.addActions(fetchedActions)

      actionsGlobalState.actions = new Set([
        ...fetchedActions,
        ...localPersistedActions,
      ])
      actionsGlobalState.fetched = true
      onboardingStateUpdatedAction.invoke({ ...actionsGlobalState })
      return true
    } catch (err) {
      return false
    }

    // Note: do not add onboardingActions to deps
  }, [actions.fetched])

  const setOnboardingActions = useCallback(async (actionNames: string[]) => {
    actionsGlobalState.actions = new Set([
      ...actionsGlobalState.actions,
      ...actionNames,
    ])
    onboardingStateUpdatedAction.invoke({ ...actionsGlobalState })

    void onboardingStorage.addActions(actionNames)
    async function setServerActions() {
      window.removeEventListener('online', setServerActions)
      try {
        await setOnboardingActionsReq(actionNames)
      } catch (err) {
        window.addEventListener('online', setServerActions)
      }
    }
    void setServerActions()
  }, [])

  const initializeActions = useCallback(async () => {
    window.removeEventListener('online', initializeActions)
    const success = await fetchActions()

    if (!success) window.addEventListener('online', initializeActions)
  }, [fetchActions])

  useEffect(() => {
    asyncDelay(2000).then(initializeActions)
  }, [initializeActions])

  useEffect(() => onboardingStateUpdatedAction.add(setActions), [])

  return { actions, setOnboardingActions }
}

/**
 *
 * @param onboardingAction
 * @param {boolean} [runAutomatically=true]
 */
export function useOnboarding(
  onboardingAction: IOnboarding,
  runAutomatically: boolean = true,
) {
  const [enabled, setEnabled] = useState(false)
  const { actions: actionsState, setOnboardingActions } = useOnboardingActions()

  const shouldRunOnboarding = useMemo(() => {
    return (
      actionsState.fetched &&
      !actionsState.actions.has(onboardingAction as string)
    )
  }, [actionsState, onboardingAction])

  const handleCompleteOnboarding = useCallback(() => {
    if (!enabled && !(runAutomatically && shouldRunOnboarding)) return
    void setOnboardingActions([onboardingAction as string])
    setEnabled(false)
  }, [
    setOnboardingActions,
    enabled,
    onboardingAction,
    shouldRunOnboarding,
    runAutomatically,
  ])

  const run = useCallback(() => {
    setEnabled(true)
  }, [])

  return {
    completeOnboarding: handleCompleteOnboarding,
    shouldRunOnboarding,
    enabled: runAutomatically ? shouldRunOnboarding : enabled,
    run,
  }
}
